import React from "react";
import styled from "styled-components";
import { SearchInput } from "@/components/UI/SearchInput";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";
import { Divider, colors2 } from "oolib";
import TooltipV2 from "@/components/UI/Tooltip";

const StyledToolbar = styled.div`
  display: flex;
  align-items: center;
  padding-block: 0.2rem;
`;

const StyledButtonWrapper = styled.div`
  flex: ${({ $hasText }) => ($hasText ? 2 : 1)};
  display: flex;
  justify-content: center;

  > * {
    width: 100%;
    justify-content: center;
  }

  ${({ $active }) => $active && `
    > button {
      background-color: ${colors2.grey10};
    }
  `}
`;

const StyledActionGroup = styled.div`
  display: flex;
  align-items: center;
`;

const ACTION_KEYS = [
  "selectAll",
  "clearAll",
  "invertSelection",
  "sort",
  "showSelectedFirst",
];


const StyledVerticalDivider = styled.div`
  width: 1px;
  height: 1.4rem;
  background: ${colors2.grey15};
  flex-shrink: 0;
`;

const StyledSearchWithClose = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 0.8rem;
  padding-right: 0.4rem;

  > *:first-child {
    flex: 1;
    min-width: 0;
  }
`;

const StyledStickyHeader = styled.div`
  position: sticky;
  top: -4px;
  z-index: 2;
  background: ${colors2.white};
  padding-top: 2px;
  margin-top: -4px;
`;

export const SearchActionsToolbar = ({
  search,
  onClose,
  selectAll,
  clearAll,
  invertSelection,
  sort,
  showSelectedFirst,
}) => {
  const actions = { selectAll, clearAll, invertSelection, sort, showSelectedFirst };
  const activeActions = ACTION_KEYS.filter((key) => actions[key]);
  const hasActions = activeActions.length > 0;

  // While a search is active, Select-all / Clear-all act on the search results, but
  // Invert has no sensible scoped meaning (it would silently drop out-of-search
  // selections), so it's disabled. This is enforced here so every consumer behaves the same.
  const searchActive = !!(search?.value && String(search.value).trim());

  return (
    <StyledStickyHeader>
      <div data-lightbox-header>
        {onClose ? (
          <StyledSearchWithClose>
            <SearchInput
              value={search.value}
              onChange={(_, v) => search.onChange(v)}
            />
            <StyledVerticalDivider style={{ marginRight: '4px' }} />
            <ButtonGhost icon="X" onClick={onClose} S style={{ padding: 4 }} />
          </StyledSearchWithClose>
        ) : (
          <SearchInput
            value={search.value}
            onChange={(_, v) => search.onChange(v)}
          />
        )}
      </div>
      <div data-lightbox-divider><Divider /></div>

      {hasActions && (() => {
        const primaryKeys = activeActions.filter((k) => k === "selectAll" || k === "clearAll");
        const secondaryKeys = activeActions.filter((k) => k !== "selectAll" && k !== "clearAll");
        return (
          <>
            <StyledToolbar data-lightbox-header style={{ justifyContent: "space-between" }}>
              {primaryKeys.length > 0 && (
                <StyledActionGroup>
                  {primaryKeys.map((key) => {
                    const { display, onClick, icon, active } = actions[key];
                    return (
                      <StyledButtonWrapper key={key} $hasText={!!display} $active={!!active}>
                        <ButtonGhost icon={icon} children={display} onClick={onClick} style={{ paddingBlock: '0.8rem' }} />
                      </StyledButtonWrapper>
                    );
                  })}
                </StyledActionGroup>
              )}
              {secondaryKeys.length > 0 && (
                <StyledActionGroup>
                  {secondaryKeys.map((key) => {
                    const { display, onClick, icon, active, tooltip } = actions[key];
                    const disabled = key === "invertSelection" && searchActive;
                    const effectiveTooltip = disabled ? "Invert isn't available while searching" : tooltip;
                    const button = (
                      <StyledButtonWrapper key={key} $hasText={!!display} $active={!!active}>
                        <ButtonGhost icon={icon} children={display} onClick={onClick} disabled={disabled} />
                      </StyledButtonWrapper>
                    );
                    return effectiveTooltip ? (
                      <TooltipV2 key={key} text={effectiveTooltip} placement="bottom">
                        {button}
                      </TooltipV2>
                    ) : button;
                  })}
                </StyledActionGroup>
              )}
            </StyledToolbar>
            <div data-lightbox-divider><Divider /></div>
          </>
        );
      })()}
    </StyledStickyHeader>
  );
};

/**
 * Builds SearchActionsToolbar props from a useListControls result + search state,
 * so every drilldown level (DocHierarchyFilter parent/child, AnnoHierarchyFilter
 * themes/subThemes/tags) wires the same Select-all / Clear-all / Invert / Sort /
 * Show-selected-first toolbar identically instead of repeating the icons/labels.
 *
 * @param {object} ctl  a useListControls(...) return
 * @param {object} opts
 * @param {string} opts.searchValue      current search input value
 * @param {function} opts.onSearch       (value) => void
 * @param {object} [opts.selectAll]      override the Select-all action (e.g. a
 *   remote select-all that widens the fetch first). Defaults to ctl.selectAll.
 */
export const makeFilterToolbarProps = (ctl, { searchValue, onSearch, selectAll } = {}) => ({
  search: { value: searchValue, onChange: onSearch },
  selectAll: selectAll || { display: "Select all", icon: "CheckCircle", onClick: ctl.selectAll },
  clearAll: { display: "Clear all", icon: "XCircle", onClick: ctl.clearAll },
  invertSelection: { icon: "Shuffle", tooltip: "Invert selection", onClick: ctl.invert },
  sort: {
    onClick: ctl.toggleSort,
    icon: "SortDescending",
    tooltip: ctl.sortOrder === "desc" ? "Sort ascending" : "Sort descending",
  },
  showSelectedFirst: {
    onClick: ctl.toggleShowSelectedFirst,
    icon: "ListNumbers",
    active: ctl.showSelectedFirst,
    tooltip: "Show selected first",
  },
});
